import type { CardDefinition, CardLayout, TokenTemplate } from './types.ts';

/**
 * Official WotC Game Changers subset for Deck Transparency & baseline bracket detection.
 */
export const GAME_CHANGER_NAMES = new Set<string>([
  'rhystic study',
  'smothering tithe',
  'cyclonic rift',
  'fierce guardianship',
  'deflecting swat',
  'demonic tutor',
  'vampiric tutor',
  'imperial seal',
  'enlightened tutor',
  'mystical tutor',
  'mana vault',
  'ancient tomb',
  'chrome mox',
  'mox diamond',
  'grim monolith',
  "gaea's cradle",
  'the one ring',
  'orcish bowmasters',
  "jeska's will",
  'underworld breach',
  "thassa's oracle",
  'ad nauseam',
  "bolas's citadel",
  'necropotence',
  'force of will',
  'pact of negation',
  'drannith magistrate',
  'grand arbiter augustin iv',
  'opposition agent',
  "teferi's protection",
  'survival of the fittest',
  'food chain',
  'seedborn muse',
  'consecrated sphinx',
]);

function scryfallImg(name: string, face?: 'front' | 'back'): string {
  const faceParam = face === 'back' ? '&face=back' : '';
  return `https://api.scryfall.com/cards/named?format=image&version=normal${faceParam}&exact=${encodeURIComponent(name)}`;
}

export const COMMON_TOKENS: Record<string, TokenTemplate> = {
  Treasure: {
    name: 'Treasure',
    typeLine: 'Token Artifact — Treasure',
    colors: [],
    oracleText: '{T}, Sacrifice this artifact: Add one mana of any color.',
  },
  Clue: {
    name: 'Clue',
    typeLine: 'Token Artifact — Clue',
    colors: [],
    oracleText: '{2}, Sacrifice this artifact: Draw a card.',
  },
  Food: {
    name: 'Food',
    typeLine: 'Token Artifact — Food',
    colors: [],
    oracleText: '{2}, {T}, Sacrifice this artifact: You gain 3 life.',
  },
  Blood: {
    name: 'Blood',
    typeLine: 'Token Artifact — Blood',
    colors: [],
    oracleText: '{1}, {T}, Discard a card, Sacrifice this artifact: Draw a card.',
  },
  Soldier: {
    name: 'Soldier',
    typeLine: 'Token Creature — Soldier',
    power: '1',
    toughness: '1',
    colors: ['W'],
  },
  Goblin: {
    name: 'Goblin',
    typeLine: 'Token Creature — Goblin',
    power: '1',
    toughness: '1',
    colors: ['R'],
  },
  Beast: {
    name: 'Beast',
    typeLine: 'Token Creature — Beast',
    power: '3',
    toughness: '3',
    colors: ['G'],
  },
  Spirit: {
    name: 'Spirit',
    typeLine: 'Token Creature — Spirit',
    power: '1',
    toughness: '1',
    colors: ['W'],
    oracleText: 'Flying',
  },
  Zombie: {
    name: 'Zombie',
    typeLine: 'Token Creature — Zombie',
    power: '2',
    toughness: '2',
    colors: ['B'],
  },
  OrcArmy: {
    name: 'Orc Army',
    typeLine: 'Token Creature — Orc Army',
    power: '0',
    toughness: '0',
    colors: ['B'],
    oracleText: 'Amass Orcs counters.',
  },
  EmblemRing: {
    name: 'The Ring',
    typeLine: 'Emblem — The Ring',
    colors: [],
    oracleText: 'Your Ring-bearer is legendary and cannot be blocked by creatures with greater power.',
  },
};

/**
 * Rich built-in Scryfall seed catalog covering all 7 special MTG layouts:
 * `normal`, `transform`, `modal_dfc`, `split`, `adventure`, `flip`, `meld`,
 * plus Partner commanders, Background commanders, Companions, Game Changers, and staples.
 */
export const SEED_CARD_CATALOG: CardDefinition[] = [
  {
    scryfallId: 'd0d33d52-3d28-4635-b985-51e126289259',
    name: "Atraxa, Praetors' Voice",
    manaCost: '{G}{W}{U}{B}',
    cmc: 4,
    typeLine: 'Legendary Creature — Phyrexian Angel Horror',
    oracleText: 'Flying, vigilance, deathtouch, lifelink\nAt the beginning of your end step, proliferate.',
    colors: ['W', 'U', 'B', 'G'],
    power: '4',
    toughness: '4',
    imageUrl: scryfallImg("Atraxa, Praetors' Voice"),
    layout: 'normal',
    setCode: 'CM2',
    collectorNumber: '10',
  },
  {
    scryfallId: 'cd9fec9d-23c8-4d35-97c1-9499527198fb',
    name: 'Krenko, Mob Boss',
    manaCost: '{2}{R}{R}',
    cmc: 4,
    typeLine: 'Legendary Creature — Goblin Warrior',
    oracleText: '{T}: Create X 1/1 red Goblin creature tokens, where X is the number of Goblins you control.',
    colors: ['R'],
    power: '3',
    toughness: '3',
    imageUrl: scryfallImg('Krenko, Mob Boss'),
    layout: 'normal',
    setCode: 'CMM',
    collectorNumber: '238',
    relatedTokens: [COMMON_TOKENS.Goblin],
  },
  {
    scryfallId: 'bc2a2d9f-b41e-4b3a-a95c-218d180cfb21',
    name: 'Tymna the Weaver',
    manaCost: '{1}{W}{B}',
    cmc: 3,
    typeLine: 'Legendary Creature — Human Cleric',
    oracleText: 'Lifelink\nAt the beginning of your postcombat main phase, you may pay X life, where X is the number of opponents that were dealt combat damage this turn. If you do, draw X cards.\nPartner',
    colors: ['W', 'B'],
    power: '2',
    toughness: '2',
    imageUrl: scryfallImg('Tymna the Weaver'),
    layout: 'normal',
    setCode: 'C16',
    collectorNumber: '48',
  },
  {
    scryfallId: '57eac5e7-b737-4e92-a90b-8c87197b14a8',
    name: "Kraum, Ludevic's Opus",
    manaCost: '{3}{U}{R}',
    cmc: 5,
    typeLine: 'Legendary Creature — Zombie Horror',
    oracleText: 'Flying, haste\nWhenever an opponent casts their second spell each turn, draw a card.\nPartner',
    colors: ['U', 'R'],
    power: '4',
    toughness: '4',
    imageUrl: scryfallImg("Kraum, Ludevic's Opus"),
    layout: 'normal',
    setCode: 'C16',
    collectorNumber: '34',
  },
  {
    scryfallId: 'dfe0daf8-43da-484b-baa1-76f8313c5a6c',
    name: 'Wilson, Refined Grizzly',
    manaCost: '{1}{G}',
    cmc: 2,
    typeLine: 'Legendary Creature — Bear Warrior',
    oracleText: 'Vigilance, reach, trample, ward {2}\nThis spell can\'t be countered.\nChoose a Background',
    colors: ['G'],
    power: '2',
    toughness: '2',
    imageUrl: scryfallImg('Wilson, Refined Grizzly'),
    layout: 'normal',
    setCode: 'CLB',
    collectorNumber: '261',
  },
  {
    scryfallId: '73d8fc6c-1d06-44c0-99fd-6ea7d12e8ca1',
    name: 'Raised by Giants',
    manaCost: '{5}{G}',
    cmc: 6,
    typeLine: 'Legendary Enchantment — Background',
    oracleText: 'Commander creatures you own have base power and toughness 10/10 and are Giants in addition to their other types.',
    colors: ['G'],
    imageUrl: scryfallImg('Raised by Giants'),
    layout: 'normal',
    setCode: 'CLB',
    collectorNumber: '250',
  },
  {
    scryfallId: '00b06b16-c103-48da-b99f-7bd6fa9b7291',
    name: 'Lurrus of the Dream-Den',
    manaCost: '{1}{W/B}{W/B}',
    cmc: 3,
    typeLine: 'Legendary Creature — Cat Nightmare',
    oracleText: 'Companion — Each permanent card in your starting deck has mana value 2 or less.\nLifelink\nOnce during each of your turns, you may cast a permanent spell with mana value 2 or less from your graveyard.',
    colors: ['W', 'B'],
    power: '3',
    toughness: '2',
    imageUrl: scryfallImg('Lurrus of the Dream-Den'),
    layout: 'normal',
    setCode: 'IKO',
    collectorNumber: '226',
  },
  {
    scryfallId: '275426c4-c14e-47d0-a9d4-24da7f6f6911',
    name: 'Yorion, Sky Nomad',
    manaCost: '{3}{W/U}{W/U}',
    cmc: 5,
    typeLine: 'Legendary Creature — Bird Serpent',
    oracleText: 'Companion — Your starting deck contains at least twenty cards more than the minimum deck size.\nFlying',
    colors: ['W', 'U'],
    power: '4',
    toughness: '5',
    imageUrl: scryfallImg('Yorion, Sky Nomad'),
    layout: 'normal',
    setCode: 'IKO',
    collectorNumber: '232',
  },
  // Special Layouts: transform, modal_dfc, adventure, split, flip, meld
  {
    scryfallId: 'abff6c81-65a4-48fa-ba8f-580f87b0344a',
    name: 'Delver of Secrets',
    manaCost: '{U}',
    cmc: 1,
    typeLine: 'Creature — Human Wizard',
    oracleText: 'At the beginning of your upkeep, look at the top card of your library. You may reveal that card. If an instant or sorcery card is revealed this way, transform Delver of Secrets.',
    colors: ['U'],
    power: '1',
    toughness: '1',
    imageUrl: scryfallImg('Delver of Secrets'),
    backName: 'Insectile Aberration',
    backTypeLine: 'Creature — Human Insect',
    backOracleText: 'Flying (3/2)',
    backImageUrl: scryfallImg('Delver of Secrets', 'back'),
    layout: 'transform',
    setCode: 'ISD',
    collectorNumber: '51',
  },
  {
    scryfallId: '09fd2d9c-1793-4beb-a3fb-7a869f660cd4',
    name: 'Bala Ged Recovery',
    manaCost: '{2}{G}',
    cmc: 3,
    typeLine: 'Sorcery',
    oracleText: 'Return target card from your graveyard to your hand.',
    colors: ['G'],
    imageUrl: scryfallImg('Bala Ged Recovery'),
    backName: 'Bala Ged Sanctuary',
    backTypeLine: 'Land',
    backOracleText: 'Bala Ged Sanctuary enters tapped.\n{T}: Add {G}.',
    backImageUrl: scryfallImg('Bala Ged Recovery', 'back'),
    layout: 'modal_dfc',
    setCode: 'ZNR',
    collectorNumber: '180',
  },
  {
    scryfallId: 'b5c9649e-9ae5-4926-bf08-71ba23aa37f1',
    name: 'Bonecrusher Giant',
    manaCost: '{2}{R}',
    cmc: 3,
    typeLine: 'Creature — Giant',
    oracleText: 'Whenever Bonecrusher Giant becomes the target of a spell, Bonecrusher Giant deals 2 damage to that spell\'s controller.',
    colors: ['R'],
    power: '4',
    toughness: '3',
    imageUrl: scryfallImg('Bonecrusher Giant'),
    backName: 'Stomp',
    backTypeLine: 'Instant — Adventure',
    backOracleText: 'Damage can\'t be prevented this turn. Stomp deals 2 damage to any target.',
    layout: 'adventure',
    setCode: 'ELD',
    collectorNumber: '115',
  },
  {
    scryfallId: 'ab8292ae-9a52-43e2-ac2e-b8368e0b0fd8',
    name: 'Fire // Ice',
    manaCost: '{1}{R} // {1}{U}',
    cmc: 4,
    typeLine: 'Instant // Instant',
    oracleText: 'Fire deals 2 damage divided as you choose among one or two targets. // Tap target permanent. Draw a card.',
    colors: ['U', 'R'],
    imageUrl: scryfallImg('Fire // Ice'),
    backName: 'Ice',
    backTypeLine: 'Instant',
    backOracleText: 'Tap target permanent.\nDraw a card.',
    layout: 'split',
    setCode: 'MH2',
    collectorNumber: '290',
  },
  {
    scryfallId: '6ee6cd34-c117-4d7e-97d1-8f8464bf0b9a',
    name: 'Akki Lavarunner',
    manaCost: '{3}{R}',
    cmc: 4,
    typeLine: 'Creature — Goblin Warrior',
    oracleText: 'Haste\nWhenever Akki Lavarunner deals damage to an opponent, flip it.',
    colors: ['R'],
    power: '1',
    toughness: '1',
    imageUrl: scryfallImg('Akki Lavarunner'),
    backName: 'Tok-Tok, Volcano Born',
    backTypeLine: 'Legendary Creature — Goblin Shaman',
    backOracleText: 'Protection from red\nIf a red source would deal damage to a player, it deals that much damage plus 1 to that player instead.',
    layout: 'flip',
    setCode: 'CHK',
    collectorNumber: '153',
  },
  {
    scryfallId: '8a1c6f89-8e8f-427b-b069-0c46c9e59a1c',
    name: 'Urza, Lord Protector',
    manaCost: '{1}{W}{U}',
    cmc: 3,
    typeLine: 'Legendary Creature — Human Artificer',
    oracleText: 'Artifact, instant, and sorcery spells you cast cost {1} less to cast.\n{7}: If you both own and control Urza, Lord Protector and an artifact named The Mightstone and Weakstone, exile them, then meld them into Urza, Planeswalker.',
    colors: ['W', 'U'],
    power: '2',
    toughness: '4',
    imageUrl: scryfallImg('Urza, Lord Protector'),
    backName: 'Urza, Planeswalker',
    backTypeLine: 'Legendary Planeswalker — Urza',
    backOracleText: 'Melded Planeswalker (Loyalty 7)',
    backImageUrl: scryfallImg('Urza, Planeswalker'),
    layout: 'meld',
    setCode: 'BRO',
    collectorNumber: '225',
  },
  // Staples & Game Changers
  {
    scryfallId: '4cbc6901-6a4a-4d0a-83ea-7eefa3b35021',
    name: 'Sol Ring',
    manaCost: '{1}',
    cmc: 1,
    typeLine: 'Artifact',
    oracleText: '{T}: Add {C}{C}.',
    colors: [],
    imageUrl: scryfallImg('Sol Ring'),
    layout: 'normal',
    setCode: 'CMM',
    collectorNumber: '400',
  },
  {
    scryfallId: '1bf50ef8-e7e8-4099-ba91-0d7c31b04541',
    name: 'Arcane Signet',
    manaCost: '{2}',
    cmc: 2,
    typeLine: 'Artifact',
    oracleText: "{T}: Add one mana of any color in your commander's color identity.",
    colors: [],
    imageUrl: scryfallImg('Arcane Signet'),
    layout: 'normal',
    setCode: 'CMM',
    collectorNumber: '367',
  },
  {
    scryfallId: 'd6914dba-0d27-4055-ac34-b3ebf5802221',
    name: 'Rhystic Study',
    manaCost: '{2}{U}',
    cmc: 3,
    typeLine: 'Enchantment',
    oracleText: 'Whenever an opponent casts a spell, you may draw a card unless that player pays {1}.',
    colors: ['U'],
    imageUrl: scryfallImg('Rhystic Study'),
    layout: 'normal',
    isGameChanger: true,
    setCode: 'WOT',
    collectorNumber: '25',
  },
  {
    scryfallId: '861b5889-0183-4bee-afeb-a4b2aa700a8e',
    name: 'Smothering Tithe',
    manaCost: '{3}{W}',
    cmc: 4,
    typeLine: 'Enchantment',
    oracleText: "Whenever an opponent draws a card, that player may pay {2}. If the player doesn't, you create a Treasure token.",
    colors: ['W'],
    imageUrl: scryfallImg('Smothering Tithe'),
    layout: 'normal',
    isGameChanger: true,
    relatedTokens: [COMMON_TOKENS.Treasure],
    setCode: 'WOT',
    collectorNumber: '13',
  },
  {
    scryfallId: '2c216e11-28ef-48c7-b686-99513483c924',
    name: 'The One Ring',
    manaCost: '{4}',
    cmc: 4,
    typeLine: 'Legendary Artifact',
    oracleText: 'Indestructible\nWhen The One Ring enters, if you cast it, you gain protection from everything until your next turn.\nAt the beginning of your upkeep, you lose 1 life for each burden counter on The One Ring.\n{T}: Put a burden counter on The One Ring, then draw a card for each burden counter on The One Ring.',
    colors: [],
    imageUrl: scryfallImg('The One Ring'),
    layout: 'normal',
    isGameChanger: true,
    setCode: 'LTR',
    collectorNumber: '246',
    relatedTokens: [COMMON_TOKENS.EmblemRing],
  },
  {
    scryfallId: '5fe58118-c74a-4cf8-981b-be0a9bf20c3b',
    name: 'Orcish Bowmasters',
    manaCost: '{1}{B}',
    cmc: 2,
    typeLine: 'Creature — Orc Archer',
    oracleText: 'Flash\nWhen Orcish Bowmasters enters and whenever an opponent draws a card except the first one they draw in each of their draw steps, Orcish Bowmasters deals 1 damage to any target, then amass Orcs 1.',
    colors: ['B'],
    power: '1',
    toughness: '1',
    imageUrl: scryfallImg('Orcish Bowmasters'),
    layout: 'normal',
    isGameChanger: true,
    setCode: 'LTR',
    collectorNumber: '103',
    relatedTokens: [COMMON_TOKENS.OrcArmy],
  },
  {
    scryfallId: '9be2bf8f-5e49-4345-81dc-08eeb7fa4fee',
    name: 'Swords to Plowshares',
    manaCost: '{W}',
    cmc: 1,
    typeLine: 'Instant',
    oracleText: 'Exile target creature. Its controller gains life equal to its power.',
    colors: ['W'],
    imageUrl: scryfallImg('Swords to Plowshares'),
    layout: 'normal',
  },
  {
    scryfallId: '8493131c-0a7b-4be6-a8a2-0b425f4f67fb',
    name: 'Counterspell',
    manaCost: '{U}',
    cmc: 2,
    typeLine: 'Instant',
    oracleText: 'Counter target spell.',
    colors: ['U'],
    imageUrl: scryfallImg('Counterspell'),
    layout: 'normal',
  },
  {
    scryfallId: 'f29ba16f-c8fb-42fe-aabf-87089cb214a7',
    name: 'Lightning Bolt',
    manaCost: '{R}',
    cmc: 1,
    typeLine: 'Instant',
    oracleText: 'Lightning Bolt deals 3 damage to any target.',
    colors: ['R'],
    imageUrl: scryfallImg('Lightning Bolt'),
    layout: 'normal',
  },
  {
    scryfallId: 'feefe9f0-24a6-461c-9ef1-86c5a6f33b83',
    name: 'Birds of Paradise',
    manaCost: '{G}',
    cmc: 1,
    typeLine: 'Creature — Bird',
    oracleText: 'Flying\n{T}: Add one mana of any color.',
    colors: ['G'],
    power: '0',
    toughness: '1',
    imageUrl: scryfallImg('Birds of Paradise'),
    layout: 'normal',
  },
  {
    scryfallId: 'e19cd13b-252c-4410-9785-1a8e0e0c5503',
    name: 'Cultivate',
    manaCost: '{2}{G}',
    cmc: 3,
    typeLine: 'Sorcery',
    oracleText: 'Search your library for up to two basic land cards, reveal those cards, put one onto the battlefield tapped and the other into your hand, then shuffle.',
    colors: ['G'],
    imageUrl: scryfallImg('Cultivate'),
    layout: 'normal',
  },
  {
    scryfallId: '0df58095-6a14-4d1d-88ec-0cf93cf13f0c',
    name: 'Tireless Provisioner',
    manaCost: '{2}{G}',
    cmc: 3,
    typeLine: 'Creature — Elf Scout',
    oracleText: 'Landfall — Whenever a land you control enters, create a Food token or a Treasure token.',
    colors: ['G'],
    power: '3',
    toughness: '2',
    imageUrl: scryfallImg('Tireless Provisioner'),
    layout: 'normal',
    relatedTokens: [COMMON_TOKENS.Food, COMMON_TOKENS.Treasure],
  },
  {
    scryfallId: '842b431d-38f0-4769-856e-9530f82e6d1a',
    name: 'Academy Manufactor',
    manaCost: '{3}',
    cmc: 3,
    typeLine: 'Artifact Creature — Assembly-Worker',
    oracleText: 'If you would create a Clue, Food, or Treasure token, instead create one of each.',
    colors: [],
    power: '1',
    toughness: '3',
    imageUrl: scryfallImg('Academy Manufactor'),
    layout: 'normal',
    relatedTokens: [COMMON_TOKENS.Clue, COMMON_TOKENS.Food, COMMON_TOKENS.Treasure],
  },
  {
    scryfallId: '09fb3ae1-82b1-432b-b4eb-281e12b86d60',
    name: 'Demonic Tutor',
    manaCost: '{1}{B}',
    cmc: 2,
    typeLine: 'Sorcery',
    oracleText: 'Search your library for a card, put that card into your hand, then shuffle.',
    colors: ['B'],
    imageUrl: scryfallImg('Demonic Tutor'),
    layout: 'normal',
    isGameChanger: true,
  },
  {
    scryfallId: '4e68eb3b-08b2-4e7e-a40b-006b2fb555e1',
    name: 'Cyclonic Rift',
    manaCost: '{1}{U}',
    cmc: 2,
    typeLine: 'Instant',
    oracleText: "Return target nonland permanent you don't control to its owner's hand.\nOverload {6}{U}",
    colors: ['U'],
    imageUrl: scryfallImg('Cyclonic Rift'),
    layout: 'normal',
    isGameChanger: true,
  },
  {
    scryfallId: '637fb044-c08f-41ce-a0d6-e3c3c5560681',
    name: 'Shardless Agent',
    manaCost: '{1}{G}{U}',
    cmc: 3,
    typeLine: 'Artifact Creature — Human Rogue',
    oracleText: 'Cascade (When you cast this spell, exile cards from the top of your library until you exile a nonland card that costs less. You may cast it without paying its mana cost. Put the exiled cards on the bottom in a random order.)',
    colors: ['G', 'U'],
    power: '2',
    toughness: '2',
    imageUrl: scryfallImg('Shardless Agent'),
    layout: 'normal',
  },
  {
    scryfallId: '14019383-0327-404b-ab4d-c6bc7fa8c5bc',
    name: 'Command Tower',
    manaCost: '',
    cmc: 0,
    typeLine: 'Land',
    oracleText: "{T}: Add one mana of any color in your commander's color identity.",
    colors: [],
    imageUrl: scryfallImg('Command Tower'),
    layout: 'normal',
  },
  {
    scryfallId: 'basic-plains',
    name: 'Plains',
    manaCost: '',
    cmc: 0,
    typeLine: 'Basic Land — Plains',
    oracleText: '({T}: Add {W}.)',
    colors: [],
    imageUrl: scryfallImg('Plains'),
    layout: 'normal',
  },
  {
    scryfallId: 'basic-island',
    name: 'Island',
    manaCost: '',
    cmc: 0,
    typeLine: 'Basic Land — Island',
    oracleText: '({T}: Add {U}.)',
    colors: [],
    imageUrl: scryfallImg('Island'),
    layout: 'normal',
  },
  {
    scryfallId: 'basic-swamp',
    name: 'Swamp',
    manaCost: '',
    cmc: 0,
    typeLine: 'Basic Land — Swamp',
    oracleText: '({T}: Add {B}.)',
    colors: [],
    imageUrl: scryfallImg('Swamp'),
    layout: 'normal',
  },
  {
    scryfallId: 'basic-mountain',
    name: 'Mountain',
    manaCost: '',
    cmc: 0,
    typeLine: 'Basic Land — Mountain',
    oracleText: '({T}: Add {R}.)',
    colors: [],
    imageUrl: scryfallImg('Mountain'),
    layout: 'normal',
  },
  {
    scryfallId: 'basic-forest',
    name: 'Forest',
    manaCost: '',
    cmc: 0,
    typeLine: 'Basic Land — Forest',
    oracleText: '({T}: Add {G}.)',
    colors: [],
    imageUrl: scryfallImg('Forest'),
    layout: 'normal',
  },
  {
    scryfallId: 'basic-wastes',
    name: 'Wastes',
    manaCost: '',
    cmc: 0,
    typeLine: 'Basic Land',
    oracleText: '({T}: Add {C}.)',
    colors: [],
    imageUrl: scryfallImg('Wastes'),
    layout: 'normal',
  },
];

const CATALOG_BY_NAME = new Map<string, CardDefinition>();
for (const c of SEED_CARD_CATALOG) {
  CATALOG_BY_NAME.set(c.name.toLowerCase(), c);
  if (c.backName) {
    CATALOG_BY_NAME.set(`${c.name} // ${c.backName}`.toLowerCase(), c);
  }
}

export function lookupSeedCard(name: string): CardDefinition | null {
  const clean = name.trim().toLowerCase();
  return CATALOG_BY_NAME.get(clean) ?? CATALOG_BY_NAME.get(clean.split('//')[0].trim()) ?? null;
}

/**
 * Computes bounded Levenshtein edit distance for fast typo suggestions on deck imports & OCR lines.
 */
export function levenshteinDistance(a: string, b: string): number {
  const s = a.toLowerCase().trim();
  const t = b.toLowerCase().trim();
  if (s === t) return 0;
  if (s.length === 0) return t.length;
  if (t.length === 0) return s.length;
  if (Math.abs(s.length - t.length) > 5) return 99;

  const dp = Array.from({ length: s.length + 1 }, (_, i) => i);
  for (let j = 1; j <= t.length; j++) {
    let prev = dp[0];
    dp[0] = j;
    for (let i = 1; i <= s.length; i++) {
      const temp = dp[i];
      const cost = s[i - 1] === t[j - 1] ? 0 : 1;
      dp[i] = Math.min(dp[i] + 1, dp[i - 1] + 1, prev + cost);
      prev = temp;
    }
  }
  return dp[s.length];
}

export function suggestClosestCardNames(
  rawQuery: string,
  extraKnownNames: readonly string[] = [],
  maxResults = 4
): CardDefinition[] {
  const q = rawQuery.trim().toLowerCase();
  if (!q) return [];

  const poolMap = new Map<string, CardDefinition>();
  for (const c of SEED_CARD_CATALOG) {
    poolMap.set(c.name.toLowerCase(), c);
  }
  for (const extra of extraKnownNames) {
    const key = extra.toLowerCase();
    if (!poolMap.has(key)) {
      poolMap.set(key, {
        scryfallId: `named:${key.replace(/[^a-z0-9]+/g, '-')}`,
        name: extra,
        manaCost: '{2}',
        cmc: 2,
        typeLine: 'Spell / Permanent',
        oracleText: '',
        colors: [],
        imageUrl: scryfallImg(extra),
        layout: 'normal',
        isGameChanger: GAME_CHANGER_NAMES.has(key),
      });
    }
  }

  const scored: { card: CardDefinition; score: number }[] = [];
  for (const card of poolMap.values()) {
    const lower = card.name.toLowerCase();
    if (lower === q) {
      scored.push({ card, score: 0 });
      continue;
    }
    if (lower.startsWith(q)) {
      scored.push({ card, score: 0.5 });
      continue;
    }
    if (lower.includes(q)) {
      scored.push({ card, score: 1 });
      continue;
    }
    const dist = levenshteinDistance(q, lower);
    if (dist <= 3) {
      scored.push({ card, score: 1 + dist });
    }
  }

  scored.sort((a, b) => a.score - b.score || a.card.name.localeCompare(b.card.name));
  return scored.slice(0, maxResults).map((s) => s.card);
}

/**
 * Normalizes an official Scryfall API card JSON object into Playtable's `CardDefinition`,
 * preserving multi-face DFC/split/adventure/flip/meld metadata and related tokens.
 */
export function normalizeScryfallApiCard(raw: Record<string, any>): CardDefinition {
  const rawLayout = String(raw.layout ?? 'normal');
  const layoutMap: Record<string, CardLayout> = {
    normal: 'normal',
    transform: 'transform',
    modal_dfc: 'modal_dfc',
    split: 'split',
    adventure: 'adventure',
    flip: 'flip',
    meld: 'meld',
  };
  const layout: CardLayout = layoutMap[rawLayout] ?? 'normal';
  const faces = Array.isArray(raw.card_faces) ? raw.card_faces : [];
  const front = faces[0] ?? raw;
  const back = faces[1] ?? null;

  const frontImage =
    raw.image_uris?.normal ??
    front?.image_uris?.normal ??
    scryfallImg(String(front?.name ?? raw.name ?? 'Unknown'));
  const backImage =
    back?.image_uris?.normal ??
    (layout === 'transform' || layout === 'modal_dfc'
      ? scryfallImg(String(front?.name ?? raw.name ?? 'Unknown'), 'back')
      : undefined);

  const relatedTokens: TokenTemplate[] = [];
  if (Array.isArray(raw.all_parts)) {
    for (const part of raw.all_parts) {
      if (part?.component === 'token' || /token|emblem/i.test(String(part?.type_line ?? ''))) {
        relatedTokens.push({
          name: String(part.name ?? 'Token'),
          typeLine: String(part.type_line ?? 'Token'),
          colors: [],
        });
      }
    }
  }

  const name = String(
    layout === 'split' ? raw.name : front?.name ?? raw.name ?? 'Unknown Card'
  );

  return {
    scryfallId: String(raw.id ?? `named:${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`),
    name,
    manaCost: String(front?.mana_cost ?? raw.mana_cost ?? ''),
    cmc: Number(raw.cmc ?? 0),
    typeLine: String(front?.type_line ?? raw.type_line ?? 'Permanent'),
    oracleText: String(front?.oracle_text ?? raw.oracle_text ?? ''),
    colors: Array.isArray(raw.colors)
      ? raw.colors
      : Array.isArray(front?.colors)
      ? front.colors
      : [],
    power: front?.power !== undefined ? String(front.power) : raw.power !== undefined ? String(raw.power) : undefined,
    toughness:
      front?.toughness !== undefined
        ? String(front.toughness)
        : raw.toughness !== undefined
        ? String(raw.toughness)
        : undefined,
    loyalty:
      front?.loyalty !== undefined
        ? String(front.loyalty)
        : raw.loyalty !== undefined
        ? String(raw.loyalty)
        : undefined,
    imageUrl: frontImage,
    backImageUrl: backImage,
    backName: back?.name ? String(back.name) : undefined,
    backTypeLine: back?.type_line ? String(back.type_line) : undefined,
    backOracleText: back?.oracle_text ? String(back.oracle_text) : undefined,
    layout,
    setCode: raw.set ? String(raw.set).toUpperCase() : undefined,
    collectorNumber: raw.collector_number ? String(raw.collector_number) : undefined,
    foil: Boolean(raw.foil && !raw.nonfoil),
    isGameChanger: Boolean(raw.game_changer) || GAME_CHANGER_NAMES.has(name.toLowerCase()),
    relatedTokens: relatedTokens.length > 0 ? relatedTokens : undefined,
  };
}

export const STANDARD_TOKEN_PRESETS: TokenTemplate[] = [
  { name: 'Treasure', typeLine: 'Token Artifact — Treasure', oracleText: '{T}, Sacrifice this artifact: Add one mana of any color.', colors: [] },
  { name: 'Clue', typeLine: 'Token Artifact — Clue', oracleText: '{2}, Sacrifice this artifact: Draw a card.', colors: [] },
  { name: 'Food', typeLine: 'Token Artifact — Food', oracleText: '{2}, {T}, Sacrifice this artifact: You gain 3 life.', colors: [] },
  { name: 'Blood', typeLine: 'Token Artifact — Blood', oracleText: '{1}, {T}, Discard a card, Sacrifice this artifact: Draw a card.', colors: [] },
  { name: 'Map', typeLine: 'Token Artifact — Map', oracleText: '{1}, {T}, Sacrifice this artifact: Target creature you control explores.', colors: [] },
  { name: 'Soldier', typeLine: 'Token Creature — Soldier', power: '1', toughness: '1', colors: ['W'] },
  { name: 'Spirit', typeLine: 'Token Creature — Spirit', oracleText: 'Flying', power: '1', toughness: '1', colors: ['W'] },
  { name: 'Zombie', typeLine: 'Token Creature — Zombie', power: '2', toughness: '2', colors: ['B'] },
  { name: 'Goblin', typeLine: 'Token Creature — Goblin', power: '1', toughness: '1', colors: ['R'] },
  { name: 'Beast', typeLine: 'Token Creature — Beast', power: '3', toughness: '3', colors: ['G'] },
  { name: 'Orc Army', typeLine: 'Token Creature — Orc Army', power: '0', toughness: '0', colors: ['B'] },
  { name: 'Copy / Clone Token', typeLine: 'Token Permanent', oracleText: 'Token copy of a permanent.', colors: [] },
];

export const UNDERCITY_ROOMS: { id: string; tier: number; name: string; effect: string }[] = [
  { id: 'secret_entrance', tier: 1, name: 'Secret Entrance', effect: 'Search your library for a basic land card, reveal it, put it into your hand, then shuffle.' },
  { id: 'forge', tier: 2, name: 'Forge', effect: 'Put two +1/+1 counters on target creature.' },
  { id: 'lost_well', tier: 2, name: 'Lost Well', effect: 'Scry 2.' },
  { id: 'trap', tier: 3, name: 'Trap!', effect: 'Target player loses 5 life.' },
  { id: 'arena', tier: 3, name: 'Arena', effect: 'Goad target creature.' },
  { id: 'stash', tier: 3, name: 'Stash', effect: 'Create a Treasure token.' },
  { id: 'archives', tier: 4, name: 'Archives', effect: 'Draw a card.' },
  { id: 'catacombs', tier: 4, name: 'Catacombs', effect: 'Create a 4/1 black Skeleton creature token with menace.' },
  { id: 'throne_of_the_dead_three', tier: 5, name: 'Throne of the Dead Three', effect: 'Reveal top 10 cards; put a creature onto battlefield with three +1/+1 counters & hexproof until next turn.' },
];

export const RING_TEMPT_LEVELS: string[] = [
  '1. Your Ring-bearer is legendary and cannot be blocked by creatures with greater power.',
  '2. Whenever your Ring-bearer attacks, draw a card, then discard a card.',
  '3. Whenever your Ring-bearer becomes blocked by a creature, that creature’s controller sacrifices it at end of combat.',
  '4. Whenever your Ring-bearer deals combat damage to a player, each opponent loses 3 life.',
];

/**
 * Auto-suggests relevant counter types from a card's type line and Oracle text.
 */
export function suggestCountersForCard(oracleText: string, typeLine: string): string[] {
  const suggestions = new Set<string>(['+1/+1', '-1/-1']);
  const lowerOracle = (oracleText ?? '').toLowerCase();
  const lowerType = (typeLine ?? '').toLowerCase();

  if (lowerType.includes('planeswalker') || lowerType.includes('battle')) {
    suggestions.add('loyalty');
  }
  if (lowerType.includes('saga')) {
    suggestions.add('lore');
  }
  if (lowerType.includes('class') || lowerType.includes('talent') || lowerOracle.includes('level up')) {
    suggestions.add('level');
  }

  const counterRegex = /\b([a-z0-9+/+-]+)\s+counters?\b/gi;
  let m: RegExpExecArray | null;
  const knownNamed = new Set([
    '+1/+1',
    '-1/-1',
    'loyalty',
    'lore',
    'charge',
    'stun',
    'shield',
    'time',
    'oil',
    'finality',
    'spore',
    'ki',
    'storage',
    'quest',
    'fade',
    'vanishing',
    'age',
    'indestructible',
    'flying',
    'lifelink',
    'deathtouch',
    'trample',
    'menace',
    'vigilance',
    'haste',
    'hexproof',
    'reach',
  ]);
  while ((m = counterRegex.exec(lowerOracle)) !== null) {
    const token = m[1].trim();
    if (knownNamed.has(token)) {
      suggestions.add(token);
    }
  }

  for (const fallback of ['charge', 'stun', 'shield', 'time']) {
    suggestions.add(fallback);
  }
  return Array.from(suggestions);
}
